import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { ViewportChangedEvent } from 'ag-grid-community'
import {
  BROKER_REQUEST_STATUS_LABELS_RU,
  canEditCustoms,
  canViewCustoms,
  type BrokerRequestStatus,
} from '@crm/shared'
import { getBrokerRequests } from '../../../shared/api/customs.api'
import { useAuthStore } from '../../auth/authStore'
import { PageLoader } from '../../../shared/ui/Spinner'
import { EmptyState } from '../../../shared/ui/EmptyState'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { BrokerGrid } from './BrokerGrid'
import { BrokerUploadButton } from './BrokerUploadButton'
import { buildJournalColDefs, toJournalRow, type BrokerJournalRow } from './brokerColDefs'
import { useCustomsBrokers } from '../brokers/useCustomsBrokers'

/** Размер порции журнала; следующая догружается при прокрутке к концу таблицы. */
const PAGE_SIZE = 200
const STATUS_ORDER: BrokerRequestStatus[] = ['SENT', 'PARTIAL', 'ANSWERED']

type Tab = BrokerRequestStatus | ''

/**
 * Журнал запросов брокеру (Л2): каждый файл, отправленный брокеру, и что по
 * нему уже ответили. Смотрят логист, ADMIN, гендиректор и РОЗ; загружает
 * ответ только логист.
 */
export function BrokerRequestsPage() {
  const role = useAuthStore((s) => s.user?.role)
  if (!canViewCustoms(role)) {
    return (
      <EmptyState
        title="Раздел недоступен"
        description="Запросы брокеру видят логист, администратор, генеральный директор и РОЗ."
      />
    )
  }
  return <BrokerRequestsView canEdit={canEditCustoms(role)} />
}

function BrokerRequestsView({ canEdit }: { canEdit: boolean }) {
  const [tab, setTab] = useState<Tab>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  /** '' — все брокеры, 'none' — без брокера, иначе id карточки. */
  const [brokerId, setBrokerId] = useState('')
  const brokers = useCustomsBrokers(true)

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const query = useInfiniteQuery({
    queryKey: ['customs', 'broker-requests', { status: tab, search, brokerId }],
    queryFn: ({ pageParam }) =>
      getBrokerRequests({
        ...(tab ? { status: tab } : {}),
        ...(search ? { search } : {}),
        ...(brokerId ? { brokerId } : {}),
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    // Без WebSocket у логиста: журнал перечитывается при открытии и возврате на вкладку.
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  // Догрузка по offset: если за время листания появился новый запрос, список сдвигается —
  // оставляем первое вхождение, чтобы в таблице не было двух строк с одним id.
  const rows = useMemo<BrokerJournalRow[]>(() => {
    const seen = new Set<string>()
    const out: BrokerJournalRow[] = []
    for (const page of query.data?.pages ?? []) {
      for (const item of page.items) {
        if (seen.has(item.id)) continue
        seen.add(item.id)
        out.push(toJournalRow(item))
      }
    }
    return out
  }, [query.data])
  const counts = query.data?.pages[0]?.counts
  const total = query.data?.pages[0]?.total ?? 0
  const allCount = counts ? STATUS_ORDER.reduce((sum, s) => sum + (counts[s] ?? 0), 0) : null

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query
  const onViewportChanged = useCallback(
    (e: ViewportChangedEvent<BrokerJournalRow>) => {
      if (!hasNextPage || isFetchingNextPage) return
      const shown = e.api.getDisplayedRowCount()
      if (shown > 0 && e.lastRow >= shown - 20) void fetchNextPage()
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  const colDefs = useMemo(() => buildJournalColDefs(), [])

  const tabs: Array<{ key: Tab; label: string; count: number | null }> = [
    { key: '', label: 'Все', count: allCount },
    ...STATUS_ORDER.map((s) => ({
      key: s as Tab,
      label: BROKER_REQUEST_STATUS_LABELS_RU[s],
      count: counts ? (counts[s] ?? 0) : null,
    })),
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-900">Запросы брокеру</h1>
          {query.data && (
            <span className="text-sm text-gray-500">
              найдено: <span className="font-medium text-gray-900">{total}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/customs"
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700"
          >
            Очередь «Нетарифка»
          </Link>
          {canEdit ? (
            <BrokerUploadButton className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50" />
          ) : (
            <span className="text-xs text-gray-500">Просмотр: правка доступна логисту</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.key || 'all'}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-brand-600 text-brand-700 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
            {t.count != null && (
              <span className="ml-1.5 text-xs text-gray-400 tabular-nums">
                {t.count.toLocaleString('ru-RU')}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white flex-1 min-w-[200px] max-w-md"
          placeholder="Поиск: код файла или партномер"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select
          aria-label="Брокер"
          value={brokerId}
          onChange={(e) => setBrokerId(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 max-w-[240px]"
        >
          <option value="">Все брокеры</option>
          {(brokers.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
              {b.hidden ? ' (скрыт)' : ''}
            </option>
          ))}
          <option value="none">Брокер не указан</option>
        </select>
        <Link to="/customs/brokers" className="text-sm text-brand-600 hover:text-brand-700">
          Брокеры
        </Link>
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            × Очистить
          </button>
        )}
      </div>

      {query.isLoading ? (
        <PageLoader label="Загружаю запросы брокеру…" />
      ) : query.isError ? (
        <ErrorMessage error={query.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={search || tab || brokerId ? 'Запросов не найдено' : 'Запросов брокеру пока нет'}
          description={
            canEdit
              ? 'Отметьте позиции в очереди «Нетарифка» и нажмите «Отправить брокеру» — здесь появится запрос и его файл.'
              : 'Запросы брокеру формирует логист в очереди «Нетарифка».'
          }
        />
      ) : (
        <>
          <BrokerGrid<BrokerJournalRow>
            rowData={rows}
            columnDefs={colDefs}
            storageKey="crm_customs_broker_cols_v2"
            className="h-[calc(100vh-270px)] min-h-[320px]"
            noRowsText="Нет запросов"
            onViewportChanged={onViewportChanged}
          />
          {isFetchingNextPage && <p className="text-center text-sm text-gray-500">Загружаю ещё…</p>}
        </>
      )}
    </div>
  )
}
